import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PenLine, Sparkles, Wand2, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import AiSteerChips, { useAiSteering } from "../AiSteerChips";
import DictationButton from "../DictationButton";
import AssistantBubble from "./AssistantBubble";
import WebsiteAiDraftPreview from "./WebsiteAiDraftPreview";
import { BookingPageDraftResponse } from "../../features/trial-requests/contracts/BookingPageDraftResponse";
import {
  draftBookingPage,
  improveBookingPage,
  updateBookingPageSettings,
} from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { selectBookingPageSettings } from "../../features/trial-requests/trial-requests-slice";
import { mergeDraftServices, toUpdateRequest } from "../../features/trial-requests/utils/bookingPage";

type Mode = "generate" | "improve";
type Step = "closed" | "menu" | "ask" | "preview";

/**
 * Floating "make my page" assistant: a corner button that walks the instructor
 * through drafting or improving the public page in two lightweight chat-like
 * steps. Nothing is saved until the proposal is applied.
 */
const WebsiteAiAssistant = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const [step, setStep] = useState<Step>("closed");
  const [mode, setMode] = useState<Mode>("generate");
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState<BookingPageDraftResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [greeting, setGreeting] = useState(false);
  const prevEnabled = useRef<boolean | null>(null);

  const chipOptions = [
    { key: "warmer", label: t("common.aiChipWarmer", "Warmer") },
    { key: "shorter", label: t("common.aiChipShorter", "Shorter") },
    { key: "professional", label: t("common.aiChipProfessional", "More professional") },
    { key: "fixMistakes", label: t("common.aiChipFixMistakes", "Fix mistakes") },
  ];
  const steering = useAiSteering(chipOptions);

  // The one gentle nudge: right after the instructor enables an empty page,
  // open the assistant on the describe step. Enabling is the trigger, so it
  // can't recur while they browse — and the X dismisses it instantly.
  useEffect(() => {
    if (!settings) return;
    const contentPresent =
      !!settings.headline || !!settings.aboutText || !!settings.introText || settings.services.length > 0;
    if (prevEnabled.current === false && settings.enabled && !contentPresent) {
      setMode("generate");
      setGreeting(true);
      setStep("ask");
    }
    prevEnabled.current = settings.enabled;
  }, [settings]);

  if (!settings?.enabled) {
    return null;
  }

  const hasContent =
    !!settings.headline || !!settings.aboutText || !!settings.introText || settings.services.length > 0;

  const openAssistant = () => {
    setError(null);
    if (hasContent) {
      setStep("menu");
    } else {
      setMode("generate");
      setStep("ask");
    }
  };

  const close = () => {
    setStep("closed");
    setDraft(null);
    setPrompt("");
    setError(null);
    setGreeting(false);
  };

  const pickMode = (next: Mode) => {
    setMode(next);
    setPrompt("");
    steering.reset();
    setError(null);
    setStep("ask");
  };

  const canSubmit = !busy && (mode === "improve" || prompt.trim().length >= 10);

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    setGreeting(false);
    try {
      const result =
        mode === "generate"
          ? await dispatch(draftBookingPage({ prompt: prompt.trim() })).unwrap()
          : await dispatch(improveBookingPage({ instructions: steering.combine(prompt) || null })).unwrap();
      setDraft(result);
      setStep("preview");
    } catch {
      setError(
        t("website.aiDraftError", "The assistant could not draft the page right now. Please try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await dispatch(
        updateBookingPageSettings({
          ...toUpdateRequest(settings),
          layout: mode === "improve" ? settings.layout : "Landing",
          headline: draft.headline,
          introText: draft.introText,
          contactIntroText: draft.contactIntroText,
          ctaText: draft.ctaText,
          aboutText: draft.aboutText,
          locationText: draft.locationText ?? settings.locationText,
          services: mergeDraftServices(settings, draft, mode),
        }),
      ).unwrap();
      toast.success(t("common.aiApplied", "AI suggestion applied."));
      close();
    } catch {
      setError(t("website.aiDraftApplyError", "Saving the draft failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const askText =
    mode === "improve"
      ? t("website.aiAskImprove", "Anything you'd like changed? This step is optional — e.g. warmer, shorter, mention online lessons.")
      : t("website.aiAskDescribe", "Tell me in a few words what you teach, for whom and where.");

  return (
    <>
      {step !== "closed" && (
        <div className="with-border fixed bottom-24 right-6 z-40 w-[22rem] max-w-[calc(100vw-3rem)] rounded-2xl bg-white p-4 shadow-xl dark:bg-primary-10">
          <div className="mb-3 flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm font-semibold text-grey-20 dark:text-white">
              <Sparkles size={16} />
              {t("website.aiAssistantTitle", "Page assistant")}
            </p>
            <button
              type="button"
              aria-label={t("website.aiAssistantClose", "Close assistant")}
              className="rounded-full p-1.5 text-grey-40 hover:bg-grey-95 hover:text-grey-20 dark:text-grey-80 dark:hover:bg-primary-20 dark:hover:text-white"
              onClick={close}
            >
              <X size={16} />
            </button>
          </div>

          {step === "menu" && (
            <div className="flex flex-col gap-2">
              <AssistantBubble text={t("website.aiMenuIntro", "What should I do with your page?")} />
              <button type="button" className="btn btn-primary justify-start" onClick={() => pickMode("improve")}>
                <Wand2 size={16} />
                {t("website.aiOptionImprove", "Improve my text")}
              </button>
              <button type="button" className="btn btn-secondary justify-start" onClick={() => pickMode("generate")}>
                <PenLine size={16} />
                {t("website.aiOptionRewrite", "Write a completely new page")}
              </button>
            </div>
          )}

          {step === "ask" && (
            <div className="flex flex-col gap-2">
              {greeting && !busy && (
                <AssistantBubble
                  text={t(
                    "website.aiEnabledNudge",
                    "Your page is on! Want me to write it for you?",
                  )}
                />
              )}
              <AssistantBubble text={busy ? t("website.aiWorking", "Writing your page…") : askText} />
              {mode === "improve" && (
                <AiSteerChips
                  options={chipOptions}
                  selected={steering.selected}
                  disabled={busy}
                  onToggle={steering.toggle}
                />
              )}
              <span className="relative block">
                <textarea
                  className="input pr-10"
                  rows={3}
                  maxLength={500}
                  value={prompt}
                  disabled={busy}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder={
                    mode === "improve"
                      ? t("website.aiImprovePlaceholder", "Optional: steer the rewrite — e.g. warmer, shorter.")
                      : t("website.aiDraftPlaceholder", "e.g. I teach piano to kids and adults in Hamburg-Altona, in person and online.")
                  }
                />
                <span className="absolute bottom-2 right-2">
                  <DictationButton
                    compact
                    disabled={busy}
                    onText={(spoken) =>
                      setPrompt((prev) => (prev ? `${prev} ${spoken}` : spoken).slice(0, 500))
                    }
                  />
                </span>
              </span>
              <div className="flex items-center justify-between gap-2">
                {hasContent ? (
                  <button
                    type="button"
                    className="text-sm text-primary-40 hover:underline dark:text-primary-90"
                    onClick={() => setStep("menu")}
                    disabled={busy}
                  >
                    {t("website.aiBack", "Back")}
                  </button>
                ) : (
                  <span />
                )}
                <button type="button" className="btn btn-primary" disabled={!canSubmit} onClick={handleSubmit}>
                  {busy
                    ? t("website.aiWorking", "Writing your page…")
                    : mode === "improve"
                      ? t("website.aiImproveNow", "Improve it")
                      : t("website.aiWriteMyPage", "Write my page")}
                </button>
              </div>
            </div>
          )}

          {step === "preview" && draft && (
            <div className="flex flex-col gap-2">
              <AssistantBubble text={t("website.aiPreviewReady", "Here's my proposal — nothing is saved until you apply it.")} />
              <WebsiteAiDraftPreview
                draft={draft}
                mode={mode}
                settings={settings}
                busy={busy}
                onApply={handleApply}
                onDiscard={() => {
                  setDraft(null);
                  setStep("ask");
                }}
              />
            </div>
          )}

          {error && <p className="field-error mt-2">{error}</p>}
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary fixed bottom-6 right-6 z-40 rounded-full shadow-lg"
        aria-expanded={step !== "closed"}
        aria-label={
          step !== "closed"
            ? t("website.aiAssistantClose", "Close assistant")
            : hasContent
              ? t("website.aiAssistantOpen", "Open page assistant")
              : undefined
        }
        onClick={step === "closed" ? openAssistant : close}
      >
        {step !== "closed" ? (
          <X size={20} />
        ) : (
          <>
            <Sparkles size={20} />
            {!hasContent && t("website.aiMakePage", "Make a page for me")}
          </>
        )}
      </button>
    </>
  );
};

export default WebsiteAiAssistant;
